export type DesignSystemTokens = {
  surface: {
    panelBackground: string;
    gridPanelBackground: string;
    gridHeaderBackground: string;
    gridFooterBackground: string;
    panelBorder: string;
    panelShadow: string;
    headerBackground: string;
    headerDivider: string;
    rowDivider: string;
    toolbarBackground: string;
    toolbarDivider: string;
  };
  state: {
    rowHover: string;
    rowSelectedGradient: string;
    rowSelectedHoverGradient: string;
    rowSelectedBorder: string;
    rowSelectedInset: string;
    rowSelectedRightBorder: string;
  };
  button: {
    outlinedBorder: string;
    outlinedBorderHover: string;
    outlinedText: string;
    outlinedBackground: string;
    outlinedBackgroundHover: string;
    primaryBorder: string;
    primaryBorderHover: string;
    primaryText: string;
    primaryGradient: string;
    primaryGradientHover: string;
    dangerBorder: string;
    dangerBorderHover: string;
    dangerText: string;
    dangerGradient: string;
    dangerGradientHover: string;
    ghostText: string;
    ghostBackground: string;
    ghostBackgroundHover: string;
    ghostBorder: string;
    disabledText: string;
    disabledBorder: string;
    disabledBackground: string;
  };
};

export const colorTokens = {
  slate800Alpha95: 'rgba(28,34,44,0.95)',
  slate850Alpha95: 'rgba(24,29,38,0.95)',
  slate900Alpha95: 'rgba(20,24,32,0.95)',
  slate950Alpha95: 'rgba(15,17,21,0.95)',
  blackAlpha45: 'rgba(0,0,0,0.45)',
  gray500Alpha08: 'rgba(158,158,158,0.08)',
  gray500Alpha10: 'rgba(158,158,158,0.1)',
  gray500Alpha14: 'rgba(158,158,158,0.14)',
  gray400Alpha12: 'rgba(189,189,189,0.12)',
  gray400Alpha18: 'rgba(189,189,189,0.18)',
  whiteAlpha02: 'rgba(255,255,255,0.02)',
  whiteAlpha015: 'rgba(255,255,255,0.015)',
  whiteAlpha01: 'rgba(255,255,255,0.01)',
  whiteAlpha03: 'rgba(255,255,255,0.03)',
  whiteAlpha04: 'rgba(255,255,255,0.04)',
  whiteAlpha05: 'rgba(255,255,255,0.05)',
  whiteAlpha06: 'rgba(255,255,255,0.06)',
  whiteAlpha08: 'rgba(255,255,255,0.08)',
  whiteAlpha09: 'rgba(255,255,255,0.09)',
  whiteAlpha12: 'rgba(255,255,255,0.12)',
  whiteAlpha22: 'rgba(255,255,255,0.22)',
  whiteAlpha34: 'rgba(255,255,255,0.34)',
  whiteAlpha35: 'rgba(255,255,255,0.35)',
  blue300Alpha92: 'rgba(144,202,249,0.92)',
  blue400Alpha08: 'rgba(66,165,245,0.08)',
  blue400Alpha25: 'rgba(66,165,245,0.25)',
  blue400Alpha38: 'rgba(66,165,245,0.38)',
  blue400Alpha42: 'rgba(66,165,245,0.42)',
  blue400Alpha62: 'rgba(66,165,245,0.62)',
  blue500Alpha16: 'rgba(33,150,243,0.16)',
  blue500Alpha30: 'rgba(33,150,243,0.3)',
  blue500Alpha38: 'rgba(33,150,243,0.38)',
  blue500Alpha74: 'rgba(33,150,243,0.74)',
  blue700Alpha22: 'rgba(25,118,210,0.22)',
  blue700Alpha30: 'rgba(25,118,210,0.3)',
  blue700Alpha84: 'rgba(25,118,210,0.84)',
  blue700Alpha80: 'rgba(25,118,210,0.8)',
  blue600Alpha82: 'rgba(30,136,229,0.82)',
  blue600Alpha28: 'rgba(30,136,229,0.28)',
  blue800Alpha84: 'rgba(21,101,192,0.84)',
  blue800Alpha28: 'rgba(21,101,192,0.28)',
  blue800Alpha76: 'rgba(21,101,192,0.76)',
  blue800Alpha80: 'rgba(21,101,192,0.8)',
  blue900Alpha20: 'rgba(13,71,161,0.2)',
  blue900Alpha76: 'rgba(13,71,161,0.76)',
  red500Alpha45: 'rgba(244,67,54,0.45)',
  red500Alpha62: 'rgba(244,67,54,0.62)',
  red700Alpha82: 'rgba(198,40,40,0.82)',
  red700Alpha78: 'rgba(198,40,40,0.78)',
  red800Alpha80: 'rgba(183,28,28,0.8)',
  red800Alpha72: 'rgba(183,28,28,0.72)',
  red900Alpha72: 'rgba(127,29,29,0.72)',
  red900Alpha78: 'rgba(140,32,32,0.78)',
  neutral300Alpha74: 'rgba(176,186,204,0.74)',
  neutral400Alpha46: 'rgba(146,156,174,0.46)',
  neutral500Alpha24: 'rgba(118,128,148,0.24)',
  whiteTextPrimary: '#f1f5fb',
  redTextSoft: '#ffdede'
} as const;

export const gradientTokens = {
  surfacePanel: `linear-gradient(160deg, ${colorTokens.slate800Alpha95}, ${colorTokens.slate900Alpha95})`,
  surfacePanelDark: `linear-gradient(160deg, ${colorTokens.slate850Alpha95}, ${colorTokens.slate950Alpha95})`,
  surfacePanelNested: `linear-gradient(160deg, ${colorTokens.slate900Alpha95}, ${colorTokens.slate950Alpha95})`,
  stateRowSelected: `linear-gradient(160deg, ${colorTokens.blue500Alpha30}, ${colorTokens.blue700Alpha30})`,
  stateRowSelectedHover: `linear-gradient(160deg, ${colorTokens.blue500Alpha38}, ${colorTokens.blue600Alpha28})`,
  buttonPrimary: `linear-gradient(160deg, ${colorTokens.blue700Alpha84}, ${colorTokens.blue800Alpha84})`,
  buttonPrimaryHover: `linear-gradient(160deg, ${colorTokens.blue600Alpha82}, ${colorTokens.blue700Alpha84})`,
  buttonDanger: `linear-gradient(160deg, ${colorTokens.red700Alpha82}, ${colorTokens.red800Alpha80})`,
  buttonDangerHover: `linear-gradient(160deg, ${colorTokens.red700Alpha78}, ${colorTokens.red900Alpha78})`
} as const;

export const shadowTokens = {
  panel: `0 20px 45px ${colorTokens.blackAlpha45}`
} as const;

export const designSystemTokens: DesignSystemTokens = {
  surface: {
    panelBackground: gradientTokens.surfacePanel,
    gridPanelBackground: gradientTokens.surfacePanelDark,
    gridHeaderBackground: colorTokens.whiteAlpha01,
    gridFooterBackground: colorTokens.whiteAlpha01,
    panelBorder: colorTokens.gray400Alpha18,
    panelShadow: shadowTokens.panel,
    headerBackground: colorTokens.gray500Alpha14,
    headerDivider: colorTokens.gray400Alpha12,
    rowDivider: colorTokens.gray500Alpha14,
    toolbarBackground: colorTokens.gray500Alpha08,
    toolbarDivider: colorTokens.gray400Alpha12
  },
  state: {
    rowHover: colorTokens.blue500Alpha16,
    rowSelectedGradient: gradientTokens.stateRowSelected,
    rowSelectedHoverGradient: gradientTokens.stateRowSelectedHover,
    rowSelectedBorder: colorTokens.blue500Alpha74,
    rowSelectedInset: colorTokens.blue300Alpha92,
    rowSelectedRightBorder: colorTokens.blue400Alpha42
  },
  button: {
    outlinedBorder: colorTokens.whiteAlpha35,
    outlinedBorderHover: colorTokens.blue400Alpha62,
    outlinedText: colorTokens.whiteTextPrimary,
    outlinedBackground: colorTokens.blue400Alpha08,
    outlinedBackgroundHover: colorTokens.blue500Alpha16,
    primaryBorder: colorTokens.blue500Alpha74,
    primaryBorderHover: colorTokens.blue300Alpha92,
    primaryText: colorTokens.whiteTextPrimary,
    primaryGradient: gradientTokens.buttonPrimary,
    primaryGradientHover: gradientTokens.buttonPrimaryHover,
    dangerBorder: colorTokens.red500Alpha45,
    dangerBorderHover: colorTokens.red500Alpha62,
    dangerText: colorTokens.redTextSoft,
    dangerGradient: gradientTokens.buttonDanger,
    dangerGradientHover: gradientTokens.buttonDangerHover,
    ghostText: colorTokens.whiteAlpha09,
    ghostBackground: colorTokens.whiteAlpha02,
    ghostBackgroundHover: colorTokens.whiteAlpha08,
    ghostBorder: colorTokens.whiteAlpha12,
    disabledText: colorTokens.neutral300Alpha74,
    disabledBorder: colorTokens.neutral400Alpha46,
    disabledBackground: colorTokens.neutral500Alpha24
  }
};
